import { useCallback, useMemo } from 'react';
import { useQueryListProjects } from '../../../../core/queries/projects.queries';
import { useMutationUpdateHub, useQueryGetHub } from '../../../lockers/hubs.queries';
import { EntityForm } from '../form.types';
import { HubSettingsForm } from './form';
import { HubSettingsFormOutput } from './form-output';
import { HubSettingsFormSchema } from './form-schema';
import { hubSettingsFormUISchema } from './ui-schema';
import { DynamicFormLocationField } from '../shared/dynamic-form-location-field';

export function useEditHubForm(hubID: string | undefined, formData: HubSettingsForm | undefined): EntityForm | null {
  const hubResult = useQueryGetHub(hubID);
  const allProjects = useQueryListProjects();
  const hubMutation = useMutationUpdateHub();

  const defaultValues = useMemo(() => {
    if (!hubResult.data) return {} as HubSettingsForm;
    return HubSettingsFormOutput.FromHubDto(hubResult.data);
  }, [hubResult.data]);

  const initialFormOutput = useMemo(() => {
    return new HubSettingsFormOutput(defaultValues);
  }, [defaultValues]);

  const currentFormOutput = useMemo(() => {
    return new HubSettingsFormOutput({
      ...initialFormOutput,
      ...formData
    });
  }, [formData, initialFormOutput]);

  const projectOptions = useMemo(() => {
    return allProjects.data ?? (hubResult.data ? [] : []);
  }, [allProjects.data, hubResult.data]);

  const formSchema = useMemo(() => {
    return HubSettingsFormSchema.FromFormData(currentFormOutput, projectOptions, true);
  }, [currentFormOutput, projectOptions]);

  const onSubmit = useCallback(
    async (formOutput: HubSettingsFormOutput) => {
      if (!hubID) return;
      const result = await hubMutation.mutateAsync({
        hubID,
        dto: HubSettingsFormOutput.ToEditHubDto(formOutput)
      });
      await hubResult.refetch();
      return { entityId: result.id };
    },
    [hubID, hubMutation, hubResult]
  );

  if (!hubID) return null;
  if (!formSchema) return null;

  return {
    isLoading: hubResult.isLoading || allProjects.isLoading,
    title: 'Edit Hub',
    subtitle: hubID,
    allowSubtitleCopy: true,
    defaultValues,
    initialFormOutput,
    formSchema,
    uiSchema: hubSettingsFormUISchema,
    fields: { locationPicker: DynamicFormLocationField },
    getOutput(formData) {
      return new HubSettingsFormOutput(formData as HubSettingsForm);
    },
    onSubmit,
    isLocked: false,
    lockedMessage: null
  };
}
